import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { patientIntakeApi } from "@/lib/patientIntakeApi";

/**
 * AuthCallback - Handles magic link authentication callback for Jema Intake
 * This component processes the Supabase magic link tokens and exchanges them
 * for patient intake session tokens via the backend API
 */
export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Extract tokens from URL hash (Supabase format)
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const accessToken = params.get("access_token");
        const refreshToken = params.get("refresh_token");
        const formUid = searchParams.get("formUid");
        const referenceUid = searchParams.get("referenceUid");

        if (!accessToken) {
          setStatus("error");
          setErrorMessage("No access token found in the callback URL");
          return;
        }

        if (!formUid || !referenceUid) {
          setStatus("error");
          setErrorMessage("Missing intake form information. Please start a new intake form.");
          return;
        }

        // Exchange tokens with backend
        const result = await patientIntakeApi.completeMagicLinkCallback({
          accessToken,
          refreshToken: refreshToken || undefined,
          formUid,
          referenceUid,
        });

        // Store tokens
        if (result.data?.accessToken) {
          localStorage.setItem("accessToken", result.data.accessToken);
          if (result.data.refreshToken) {
            localStorage.setItem("refreshToken", result.data.refreshToken);
          }
        }

        setStatus("success");

        // Redirect to Jema Manager
        setTimeout(() => {
          const redirectUrl = result.data?.redirectUrl || 
            import.meta.env.VITE_JEMA_MANAGER_URL || 
            "http://localhost:5173";
          window.location.href = redirectUrl;
        }, 2000);
      } catch (error) {
        console.error("Magic link callback error:", error);
        setStatus("error");
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to authenticate. Please try again."
        );
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
            <CardTitle>Verifying your email...</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground">
              Please wait while we complete your authentication.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle>Email Verified!</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Your email has been successfully verified. Redirecting to your dashboard...
            </p>
            <div className="flex justify-center">
              <div className="animate-pulse text-sm text-muted-foreground">Redirecting...</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <CardTitle>Authentication Failed</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            {errorMessage || "Unable to verify your email with this link."}
          </p>
          <div className="space-y-2">
            <Button onClick={() => navigate("/")} className="w-full bg-[#166B93] hover:bg-[#125a7a]">
              Start New Intake
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            If you continue to have issues, please contact support or use the verification code
            option.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
